import React, { useCallback, useEffect, useRef, useState } from 'react';

import { classnames, handleFileSend, formatFileSize, getTrans } from '@/utils/tools';
import { bridgeCallFunc, getChildInject } from '@/utils/instantLoaded/tools';
import { Copywriting, GetMsgResCode, TRACKING_MSG_TYPE, TRACKING_OPERATION, TRACKING_TARGET, TRACKING_TYPE } from '@/utils/constants';
import { dispatchTrack } from '@/utils/tracking';
import { useUpload } from '@/utils/UploadManager';
import * as Toast from '@/utils/Toast';
import { postChatMsg } from '@/utils/agent';
import { FileUploadProps, FileIconType } from '@/types/components/FileUpload';
import { observer, inject } from 'mobx-react';
import { UploadFileStatus } from '@/stores/agent';
import { addObserver, removeObserver } from '@/utils/intersectionObserver';
import { previewWithBridge } from '@/utils/previewWithBridge';

import MaskProgress from '@/components/MaskProgress';
import FileIconCSV from '@/assets/img/fileType/file_csv.svg';
import FileIconDOC from '@/assets/img/fileType/file_doc.svg';
import FileIconDOCX from '@/assets/img/fileType/file_docx.svg';
import FileIconPDF from '@/assets/img/fileType/file_pdf.svg';
import FileIconXLS from '@/assets/img/fileType/file_xls.svg';
import FileIconXLSM from '@/assets/img/fileType/file_xlsm.svg';
import FileIconXLSX from '@/assets/img/fileType/file_xlsx.svg';
import FileIconDefault from '@/assets/img/fileType/file_default.svg';
import loadingCircle from '@/assets/img/loading_circle.png';
import msgFailure from '@/assets/img/msg_failure.png';
import { getConfig } from '@/config/core';
import reporter from '@/utils/report';

import './style.scss';

const PREFIX = 'file-upload-container';
const FILE_ICON: FileIconType = { FileIconCSV, FileIconDOC, FileIconDOCX, FileIconPDF, FileIconXLS, FileIconXLSM, FileIconXLSX };

function UIFileUpload(props: FileUploadProps) {
  const { type = '', title, size, from, percent, error, handleFileError, sendMsgError, sendMsgFinish, fileUrl } = props;
  const isFromUser = from === 'user';
  const trackingname = TRACKING_TYPE.FILE;
  const trackingdata = {
    chat_session_id: props.initStore.sessionId,
    if_history: props.isHistory ? TRACKING_MSG_TYPE.HISTORY : TRACKING_MSG_TYPE.REAL_TIME,
    is_sender: isFromUser ? 'true' : 'false',
    file_id: fileUrl,
  };

  const maskRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLDivElement>(null);

  const getFileIcon = useCallback((type: string) => {
    return <img className="info-type-icon" src={FILE_ICON[`FileIcon${type.toUpperCase()}`] || FileIconDefault} />;
  }, []);

  const handlePreview = useCallback(() => {
    const config = getConfig();
    const version = window.getAppVersion();

    if (version >= config?.fileUploadVersion && fileUrl) {
      dispatchTrack({
        operation: TRACKING_OPERATION.CLICK,
        target: TRACKING_TARGET[trackingname],
        data: trackingdata,
      });
      previewWithBridge(fileUrl, title);
    } else {
      bridgeCallFunc(() => {
        Toast.error(getTrans(Copywriting.FILE_PREVIEW_IN_OLD_VERSION));
      });
    }
  }, [fileUrl]);

  useEffect(() => {
    if (fileUrl) {
      addObserver(fileRef.current);
    }

    return () => {
      removeObserver(fileRef.current);
    };
  }, [fileUrl]);

  return (
    <div
      ref={fileRef}
      data-trackingname={trackingname}
      data-trackingdata={JSON.stringify(trackingdata)}
      className={classnames(PREFIX, { [`${PREFIX}-user`]: isFromUser })}
      onClick={handlePreview}
    >
      <div className="file-upload-info">
        <div className="info-type">
          <MaskProgress ref={maskRef} value={percent} />
          {type && getFileIcon(type)}
        </div>
        <div className="info-filename">
          {title}
        </div>
      </div>
      <div className="file-upload-size">
        {formatFileSize(size, 1)}
      </div>
      {
        isFromUser
          ? (
            (sendMsgFinish !== true && (!error && !sendMsgError))
              ? <div className="status-icon loading"><img src={loadingCircle} /></div>
              : (error || sendMsgError) && <div className="status-icon" onClick={handleFileError}><img src={msgFailure} /></div>
          )
          : null
      }
    </div>
  );
}

function FileUpload(props: FileUploadProps) {
  const { file, agentStore, title, size, curPos, fileUrl: networkUrl } = props;

  // 上传成功退出会话，再次回来时不触发上传
  if (!(file instanceof File) && networkUrl) {
    return (
      <UIFileUpload
        {...props}
        percent={100}
        finish={true}
        sendMsgFinish={true}
        fileUrl={networkUrl}
      />
    );
  }

  const { progress, error, finish, fileUrl, setError, setProgress, abort } = useUpload(file, true);
  const [sendMsgError, setSendMsgError] = useState<Error | undefined>(undefined);
  const [sendMsgFinish, setSendMsgFinish] = useState(false); // 是否发送消息成功

  const handleFileError = useCallback((event) => {
    event.stopPropagation();

    // 会话结束 则不处理
    if (agentStore.agentEndedReason) {
      return;
    }

    // 文件上传报错
    if (error && curPos) {
      const newFile = new File([props.file as File], title);
      handleFileSend(newFile, curPos);
      setError(undefined);

      return;
    }

    // 发送消息报错
    if (sendMsgError) {
      // 用户主动重发数
      reporter.apiBatchReport({ type: 'count', value: { label: 'channel_chat_message_user_resend_total' }});
      handleSendChatMsg();
    }
  }, [error, curPos, sendMsgError]);

  const handleSendChatMsg = useCallback(() => {
    setSendMsgFinish(false);
    setSendMsgError(undefined);
    agentStore.UPDATE_FILE_UPLOADING_COUNT(UploadFileStatus.UPLOADING);

    postChatMsg({
      text: fileUrl,
      type: 'FILE',
      params: {
        url: fileUrl,
        filename: title,
        name: title,
        size,
      },
    }).then(({ code }) => {
      agentStore.UPDATE_FILE_UPLOADING_COUNT(UploadFileStatus.UPLOADED);
      if (code === GetMsgResCode.SUCCESS) {
        setSendMsgError(undefined);
        setSendMsgFinish(true);

        // 消息发送成功 才算成功
        agentStore.UPDATE_AGENT_DATA_ITEM(
          { fileUrl },
          curPos,
        );
      } else {
        setSendMsgError(new Error('send message failed'));
        setSendMsgFinish(false);
      }
    });
  }, [agentStore.sessionKey, fileUrl, title, size, curPos]);

  // 会话结束
  useEffect(() => {
    if (agentStore.agentEndedReason) {
      if (finish !== true) {
        abort();
        setProgress(0);
        setError(new Error('session end'));
      }
    }
  }, [agentStore.agentEndedReason]);

  // 上传完成，发消息
  useEffect(() => {
    if (finish === true) {
      handleSendChatMsg();
    }
  }, [finish]);

  return (
    <UIFileUpload
      {...props}
      percent={progress * 100}
      error={error}
      finish={finish}
      sendMsgError={sendMsgError}
      sendMsgFinish={sendMsgFinish}
      handleFileError={handleFileError}
    />
  );
}

export default inject(getChildInject('initStore', 'agentStore'))(observer(FileUpload));
